<template>
  <div class="page page-with-bg">
    <div class="page-form-wrap">
      <div class="page-form-header small-header">
        <h1>Diabetic Risk Assessment Survey</h1>
      </div>
      <div class="page-form-content">
        <horizontal-stepper
          class="custom-stepper"
          ref="stepper"
          :steps="steps">
        </horizontal-stepper>
      </div>
      <div class="page-form-footer">
        <b-form-group class="form-buttons" :class="{'form-buttons-back': currentStep > 0}">
          <b-btn class="btn-back" variant="link text-secondary" @click="goBack" v-if="currentStep > 0">Back</b-btn>
          <span>
            <b-btn variant="success" @click="next" :disabled="loading">{{ buttonText }}</b-btn>
            <b-btn variant="link text-secondary" @click="save" :disabled="loading">Save to complete later</b-btn>
          </span>
        </b-form-group>
      </div>
    </div>
  </div>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex';
  const { mapActions: mapSurveyActions, mapState: mapSurveyState } = createNamespacedHelpers('survey');
  const { mapMutations: mapUserMutations, mapActions: mapUserActions } = createNamespacedHelpers('user');

  import SurveyStepAddress from './survey-step-address.vue';
  import SurveyStepGender from './survey-step-gender.vue';
  import SurveyStepBirthdate from './survey-step-birthdate.vue';
  import SurveyStepParentsDiabet from './survey-step-parents-diabet.vue';
  import SurveyStepDiabetic from './survey-step-diabetic.vue';
  import SurveyStepMedication from './survey-step-medication.vue';
  import SurveyStepPregnancy from './survey-step-pregnancy.vue';
  import SurveyStepPayment from './../survey-step-payment.vue';

  import genderType from 'enums/gender';
  import billingMethodType from 'enums/billing-method';

  import { clientRole, clientUserRole, clientProviderRole } from "@/constants";

  import jwtDecode from 'jwt-decode';

  export default {
    data() {
      return {
        currentStep: 0,
        steps: [
          {
            id: "address",
            component: SurveyStepAddress,
            active: true
          },
          {
            id: "gender",
            component: SurveyStepGender,
            active: true
          },
          {
            id: "birthdate",
            component: SurveyStepBirthdate,
            active: true
          },
          {
            id: "parents-diabet",
            component: SurveyStepParentsDiabet,
            active: true
          },
          {
            id: "diabetic",
            component: SurveyStepDiabetic,
            active: true
          },
          {
            id: "medication",
            component: SurveyStepMedication,
            active: true
          },
          {
            id: "pregnancy",
            component: SurveyStepPregnancy,
            active: true
          },
          {
            id: "payment",
            component: SurveyStepPayment,
            active: true
          }
        ],

        genderType: genderType,
        buttonText: 'Continue',
        nextButtonText: "Continue",
        payButtonText: 'Pay Now',
        loading: false
      }
    },

    computed: {
      ...mapSurveyState(["survey"])
    },

    methods: {
      ...mapSurveyActions(["update"]),
      ...mapUserMutations(["setShowProfile"]),
      ...mapUserActions(["loginBackAsAdmin"]),

      findStepIndexById(id) {
        const object = this.steps.find(step => step.id === id);

        return this.steps.indexOf(object);
      },

      setStep(index) {
        this.$refs.stepper.setStep(index);
      },

      async save(pushRoute = true) {
        await this.update();

        if (!pushRoute)
          return;

        this.$root.successToast("Survey saved!");

        let loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
        let userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        if (userAuth.patient_info_hidden) {
          this.setShowProfile(false);
          await this.$auth.logout("/");

          return;
        }

        if (loggedInUser && loggedInUser.roles.some(r => r === clientRole || r === clientUserRole || r === clientProviderRole) && !userAuth.patient_info_hidden) {
          await this.loginBackAsAdmin();

          this.$router.push({ path: "/reports" });

          return;
        }

        this.$router.push("survey-incompleted");
      },

      async next() {
        this.loading = true;

        await this.$refs.stepper.next();

        this.updateNextButtonText();

        if (this.$refs.stepper.finalStep)
          await this.completeSurvey();
        else
          await this.save(false);

        this.loading = this.survey.isCompleted;
      },
      async completeSurvey() {
        this.survey.isCompleted = true;
        await this.update();
        this.$root.successToast("Survey completed!");
        this.$router.push("survey-completed");

        let userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        if (userAuth.patient_info_hidden) {
          this.setShowProfile(false);
          await this.$auth.logout("/");
        }
      },
      updateNextButtonText() {
        this.buttonText = this.$refs.stepper.currentStep.id === 'payment' ? this.payButtonText : this.nextButtonText;
      },
      goBack() {
        this.$refs.stepper.back();
        this.updateNextButtonText();
      }
    },
    watch: {
      'survey.gender': {
        immediate: true,
        handler(gender) {
          const index = this.findStepIndexById('pregnancy');
          const pregnancyStep = this.steps[index];
                pregnancyStep.active = gender === genderType.Female;
        },
      },
      'survey.reportInvoice.billingMethod': {
        immediate: true,
        handler(method) {
          const index = this.findStepIndexById('payment');
          const paymentStep = this.steps[index];
                paymentStep.active = method === billingMethodType.IndividualBilling;
        }
      }
    },
    mounted() {
      this.$watch(
        () => {
          this.currentStep = this.$refs.stepper?.currentStep?.index || 0;
          return this.$refs.stepper.currentStep;
        }, 
        (step) => {
          const stepIndex = step?.index || 0;
          this.currentStep = stepIndex;
          this.survey.lastStep = stepIndex;
        }
      );
    }
  }
</script>
<style lang="scss" scoped>
  @import '@/styles/mixins';

  .page {
    @include retina('/png/table-coffee.png');
  }
</style>